import { Popover, ActionIcon, Stack, Group, Text, Switch, Select, Tooltip, Box } from '@mantine/core';
import { IconAdjustments } from '@tabler/icons-react';
import { SMOOTHING_OPTIONS, type SmoothingWindow } from './utils/smoothing';

export interface ChartOptionsPopoverProps {
  viewMode: 'stacked' | 'combined';
  normalized: boolean;
  onNormalizedChange: (value: boolean) => void;
  smoothing: SmoothingWindow;
  onSmoothingChange: (value: SmoothingWindow) => void;
  hasEvents: boolean;
  eventLines: boolean;
  onEventLinesChange: (value: boolean) => void;
}

export function ChartOptionsPopover({
  viewMode,
  normalized,
  onNormalizedChange,
  smoothing,
  onSmoothingChange,
  hasEvents,
  eventLines,
  onEventLinesChange,
}: ChartOptionsPopoverProps): JSX.Element {
  const isActive = smoothing !== 'off' || !normalized || (hasEvents && !eventLines);

  return (
    <Popover
      position="bottom-end"
      withArrow
      shadow="md"
      width={240}
    >
      <Popover.Target>
        <Tooltip label="Chart options" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Chart options"
            style={{ position: 'relative' }}
          >
            <IconAdjustments size={20} />
            {isActive && (
              <Box
                data-testid="chart-options-active-dot"
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'var(--mantine-color-teal-6)',
                  border: '2px solid var(--mantine-color-body)',
                }}
              />
            )}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <div>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
              Smoothing
            </Text>
            <Select
              size="xs"
              value={smoothing}
              onChange={(v) => onSmoothingChange((v as SmoothingWindow) ?? 'off')}
              data={SMOOTHING_OPTIONS}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              aria-label="Smoothing window"
            />
          </div>
          {viewMode === 'combined' && (
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm">Normalized</Text>
              <Switch
                size="xs"
                checked={normalized}
                onChange={(e) => onNormalizedChange(e.currentTarget.checked)}
                aria-label="Toggle normalized values"
              />
            </Group>
          )}
          {hasEvents && (
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm">Event Lines</Text>
              <Switch
                size="xs"
                checked={eventLines}
                onChange={(e) => onEventLinesChange(e.currentTarget.checked)}
                aria-label="Show event lines on charts"
              />
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
